import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRightLeft,
  X,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Table2,
  FileBarChart,
  Plus,
  AlertOctagon,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Download,
} from 'lucide-react';
import { Button, Card, Select, TextView } from '@/shims/uiCommonComponents';
import {
  type IntercompanyReconciliationReport,
  type IntercompanySummary,
  type IntercompanyTransaction,
  type Period,
} from '@/types';
import { periodDisplayName } from '@/types';
import { periodsApi, intercompanyApi, entitiesApi } from '@/api/client';
import type { Entity } from '@/types';
import { useAppStore } from '@/store';
import { ICP_TRANSACTION_TYPES, getTransactionTypeLabel } from '@/constants/intercompany';

const PAGE_SIZE = 20;
const EXPORT_PAGE_SIZE = 500;
const ICP_MAPPING_SOURCE_SYSTEM = 'FCCS';

function escapeCsvCell(value: unknown): string {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

async function fetchAllIntercompanyForPeriod(
  periodId: string,
  workQueueOnly: boolean,
  entityId?: string
): Promise<IntercompanyTransaction[]> {
  const rows: IntercompanyTransaction[] = [];
  let page = 1;
  while (true) {
    const res = await intercompanyApi.list({
      period_id: periodId,
      page,
      page_size: EXPORT_PAGE_SIZE,
      work_queue_only: workQueueOnly,
      ...(entityId ? { entity_id: entityId } : {}),
    });
    rows.push(...res.items);
    if (rows.length >= res.total || res.items.length === 0) break;
    page += 1;
  }
  return rows;
}

function intercompanyRowsToExportRecords(rows: IntercompanyTransaction[]) {
  return rows.map((txn) => ({
    ID: txn.id,
    'From entity': txn.entity_from_name ?? txn.entity_from_id ?? '',
    'To entity': txn.entity_to_name ?? txn.entity_to_id ?? '',
    Amount: txn.amount,
    Currency: txn.currency ?? '',
    Type: getTransactionTypeLabel(txn.transaction_type ?? ''),
    'Transaction date': txn.transaction_date ?? '',
    Reference: txn.reference ?? '',
    'Invoice ref': txn.invoice_reference ?? '',
    Description: txn.description ?? '',
    'Match status': txn.match_status ?? '',
    'Group account': txn.group_account_code ?? '',
  }));
}

function downloadCsv(filename: string, rows: ReturnType<typeof intercompanyRowsToExportRecords>) {
  const headers = Object.keys(rows[0] ?? {});
  if (headers.length === 0) {
    const defaultHeaders = [
      'ID',
      'From entity',
      'To entity',
      'Amount',
      'Currency',
      'Type',
      'Transaction date',
      'Reference',
      'Invoice ref',
      'Description',
      'Match status',
      'Group account',
    ];
    const bom = '\ufeff';
    const line = defaultHeaders.map(escapeCsvCell).join(',');
    const blob = new Blob([bom + line + '\n'], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }
  const bom = '\ufeff';
  const headerLine = headers.map(escapeCsvCell).join(',');
  const dataLines = rows.map((r) => headers.map((h) => escapeCsvCell((r as Record<string, unknown>)[h])).join(','));
  const csv = [headerLine, ...dataLines].join('\n');
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Intercompany() {
  const { addNotification } = useAppStore();

  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<string>('');

  const [icTransactions, setIcTransactions] = useState<IntercompanyTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState<IntercompanySummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showReconciliation, setShowReconciliation] = useState(false);
  const [reconciliationReport, setReconciliationReport] = useState<IntercompanyReconciliationReport | null>(null);
  const [reconciliationError, setReconciliationError] = useState<string | null>(null);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  const [createDisputesLoading, setCreateDisputesLoading] = useState(false);
  const [eliminationCheckLoading, setEliminationCheckLoading] = useState(false);
  const [worksheetDownloading, setWorksheetDownloading] = useState(false);
  const [showClearedHistory, setShowClearedHistory] = useState(false);
  /** Empty = all companies; otherwise filter lines where entity is on either side (API entity_id). */
  const [filterCompanyId, setFilterCompanyId] = useState('');
  const [exportListLoading, setExportListLoading] = useState(false);

  const [syncTbLoading, setSyncTbLoading] = useState(false);
  const autoSyncedPeriodRef = useRef<string | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addForm, setAddForm] = useState({
    entity_from_id: '' as string | number,
    entity_to_id: '' as string | number,
    amount: '',
    transaction_type: 'sale',
    description: '',
  });
  const shouldLoad = selectedFilter && selectedFilter !== '';

  // Load entities for company filter and Add modal
  useEffect(() => {
    if (!shouldLoad && !showAddModal) return;
    entitiesApi
      .list({ page: 1, page_size: 500 })
      .then((r) => setEntities(r.items))
      .catch(() => setEntities([]));
  }, [shouldLoad, showAddModal]);

  // Load periods
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const periodsRes = await periodsApi.list();
        setPeriods(periodsRes);
      } catch (e) {
        const apiErr = e as { detail?: string; message?: string };
        setError(apiErr.detail || apiErr.message || 'Failed to load periods.');
      }
    };
    loadFilters();
  }, []);

  const effectivePeriodId = selectedFilter;

  useEffect(() => {
    setFilterCompanyId('');
    setPage(1);
  }, [selectedFilter]);

  const refreshData = useCallback(async () => {
    if (!effectivePeriodId) return;
    try {
      const [listRes, sum] = await Promise.all([
        intercompanyApi.list({
          period_id: effectivePeriodId,
          page,
          page_size: PAGE_SIZE,
          work_queue_only: !showClearedHistory,
          ...(filterCompanyId ? { entity_id: filterCompanyId } : {}),
        }),
        intercompanyApi.summary({ period_id: effectivePeriodId }),
      ]);
      setIcTransactions(listRes.items);
      setTotal(listRes.total);
      setSummary(sum);
    } catch {
      // ignore
    }
  }, [effectivePeriodId, page, showClearedHistory, filterCompanyId]);

  useEffect(() => {
    const loadData = async () => {
      if (!shouldLoad) {
        setIcTransactions([]);
        setSummary(null);
        setTotal(0);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const [listRes, sum] = await Promise.all([
          intercompanyApi.list({
            period_id: selectedFilter,
            page,
            page_size: PAGE_SIZE,
            work_queue_only: !showClearedHistory,
            ...(filterCompanyId ? { entity_id: filterCompanyId } : {}),
          }),
          intercompanyApi.summary({ period_id: selectedFilter }),
        ]);
        setIcTransactions(listRes.items);
        setTotal(listRes.total);
        setSummary(sum);
      } catch (e) {
        const apiErr = e as { detail?: string; message?: string };
        setError(apiErr.detail || apiErr.message || 'Failed to load intercompany data.');
        setIcTransactions([]);
        setSummary(null);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [shouldLoad, selectedFilter, page, showClearedHistory, filterCompanyId]);

  useEffect(() => {
    if (!selectedFilter || autoSyncedPeriodRef.current === selectedFilter) {
      return;
    }

    autoSyncedPeriodRef.current = selectedFilter;

    const syncSelectedPeriod = async () => {
      setSyncTbLoading(true);
      try {
        await intercompanyApi.syncFromTrialBalance(
          selectedFilter,
          ICP_MAPPING_SOURCE_SYSTEM
        );
        await refreshData();
      } catch (e) {
        autoSyncedPeriodRef.current = null;
        const err = e as { detail?: string | string[]; message?: string };
        const message = Array.isArray(err.detail)
          ? err.detail.join('; ')
          : (err.detail as string) || err.message || 'Failed to auto-sync intercompany from trial balance.';
        addNotification({
          type: 'error',
          title: 'Sync Failed',
          message,
        });
      } finally {
        setSyncTbLoading(false);
      }
    };

    void syncSelectedPeriod();
  }, [selectedFilter, refreshData, addNotification]);

  const periodLabel = periods.find((p) => String(p.id) === effectivePeriodId);

  const clearedPairsDisplay =
    summary?.cleared_pair_count ??
    (summary?.netted_leg_count != null ? Math.floor(Number(summary.netted_leg_count) / 2) : 0);

  const openLegAmountDisplay = summary
    ? Number(summary.open_leg_amount ?? summary.unmatched_amount ?? 0).toLocaleString()
    : '0.00';

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const tableBusy = isLoading || syncTbLoading;

  /** Prefer entities flagged for ICP; if none are flagged, list all (legacy data). */
  const icpCompanyFilterOptions = useMemo(() => {
    const flagged = entities.filter((e) => e.enable_intercompany === true);
    return flagged.length > 0 ? flagged : entities;
  }, [entities]);

  const getErrorMessage = (e: unknown): string => {
    const err = e as { detail?: string | string[]; message?: string };
    if (Array.isArray(err.detail)) return err.detail.join('; ');
    return (err.detail as string) || err.message || 'An error occurred';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <TextView variant="heading-1">Intercompany (ICP)</TextView>
          <TextView variant="body-sm" className="text-gray-600 mt-1 max-w-2xl">
            Selecting a month automatically syncs ICP lines from the trial balance. Review open balances
            here, then run consolidation to post eliminations.
          </TextView>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="secondary"
            onClick={async () => {
              if (!effectivePeriodId) return;
              setCreateDisputesLoading(true);
              try {
                const res = await intercompanyApi.createDisputesFromUnmatched(String(effectivePeriodId));
                addNotification({ type: 'success', title: 'Disputes Created', message: res.message });
                await refreshData();
              } catch (e) {
                addNotification({ type: 'error', title: 'Create Failed', message: getErrorMessage(e) });
              } finally {
                setCreateDisputesLoading(false);
              }
            }}
            disabled={!effectivePeriodId || createDisputesLoading || syncTbLoading}
          >
            {createDisputesLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertOctagon className="w-4 h-4 mr-2" />}
            Create Disputes
          </Button>
          <Button
            variant="secondary"
            onClick={async () => {
              if (!effectivePeriodId) return;
              setEliminationCheckLoading(true);
              try {
                const res = await intercompanyApi.eliminationCompleteness(String(effectivePeriodId));
                addNotification({
                  type: res.is_complete ? 'success' : 'info',
                  title: res.is_complete ? 'Mirror check passed' : 'Mirror legs incomplete',
                  message: res.message,
                });
              } catch (e) {
                addNotification({ type: 'error', title: 'Check Failed', message: getErrorMessage(e) });
              } finally {
                setEliminationCheckLoading(false);
              }
            }}
            disabled={!effectivePeriodId || eliminationCheckLoading || syncTbLoading}
            title="Verify each open ICP line has a matching mirror leg; run IC consolidation to post eliminations"
          >
            {eliminationCheckLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Check Elimination
          </Button>
          <Button
            variant="secondary"
            onClick={async () => {
              if (!effectivePeriodId) return;
              setShowReconciliation(true);
              setReconciliationReport(null);
              setReconciliationError(null);
              setReconciliationLoading(true);
              try {
                const report = await intercompanyApi.reconciliationReport(String(effectivePeriodId));
                setReconciliationReport(report);
              } catch (e) {
                setReconciliationReport(null);
                setReconciliationError(getErrorMessage(e));
              } finally {
                setReconciliationLoading(false);
              }
            }}
            disabled={!effectivePeriodId}
          >
            <FileBarChart className="w-4 h-4 mr-2" />
            Reconciliation Report
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowAddModal(true)}
            disabled={!effectivePeriodId}
            title="Optional manual or adjusting entry"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add (manual)
          </Button>
        </div>
      </div>

      {/* Period filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <TextView variant="label-xs">Period</TextView>
          <Select
            value={selectedFilter}
            onValueChange={(val) => {
              setSelectedFilter(val ?? '');
              setPage(1);
            }}
            options={
              periods.length === 0
                ? [{ value: '', label: 'Loading...' }]
                : periods.map((p) => ({ value: String(p.id), label: periodDisplayName(p) }))
            }
            placeholder={periods.length === 0 ? undefined : 'Select period'}
            fullWidth={false}
          />
        </div>
        {periodLabel && (
          <span className="inline-flex items-center text-xs text-gray-500">
            <ArrowRightLeft className="w-3.5 h-3.5 mr-1 text-gray-400" />
            Viewing ICP for {periodDisplayName(periodLabel)}
          </span>
        )}
        <div className="flex items-center gap-2 min-w-[min(100%,220px)] max-w-xs">
          <TextView variant="label-xs" className="whitespace-nowrap">
            ICP company
          </TextView>
          <Select
            value={filterCompanyId}
            onValueChange={(val) => {
              setFilterCompanyId(val ?? '');
              setPage(1);
            }}
            title="All ICP companies: all intercompany lines for this period. Or pick one ICP entity (from or to)."
            options={
              !shouldLoad
                ? [{ value: '', label: 'Select a period first' }]
                : entities.length === 0
                  ? [{ value: '', label: 'Loading ICP companies…' }]
                  : [
                      { value: '', label: 'All ICP companies' },
                      ...icpCompanyFilterOptions.map((e) => ({
                        value: String(e.id),
                        label: `${e.name} (${e.code})`,
                      })),
                    ]
            }
            fullWidth
            disabled={!shouldLoad}
          />
        </div>
        <label className="inline-flex items-center gap-2 min-h-[44px] px-1 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={showClearedHistory}
            onChange={(e) => {
              setShowClearedHistory(e.target.checked);
              setPage(1);
            }}
          />
          Show cleared history
        </label>
        <Button
          variant="secondary"
          disabled={!effectivePeriodId || exportListLoading || syncTbLoading}
          onClick={async () => {
            if (!effectivePeriodId) return;
            setExportListLoading(true);
            try {
              const raw = await fetchAllIntercompanyForPeriod(
                effectivePeriodId,
                !showClearedHistory,
                filterCompanyId || undefined
              );
              const records = intercompanyRowsToExportRecords(raw);
              const safePeriod = periodLabel
                ? periodDisplayName(periodLabel).replace(/[/\\?%*:|"<>]/g, '-')
                : 'period';
              const companyPart = filterCompanyId
                ? `_${entities.find((e) => String(e.id) === filterCompanyId)?.code ?? filterCompanyId}`
                : '';
              downloadCsv(`icp_${safePeriod}${companyPart}_${new Date().toISOString().slice(0, 10)}.csv`, records);
              addNotification({
                type: 'success',
                title: 'Export ready',
                message:
                  records.length === 0
                    ? 'No rows matched; downloaded a header-only CSV.'
                    : `Downloaded ${records.length} line(s) as CSV.`,
              });
            } catch (e) {
              addNotification({
                type: 'error',
                title: 'Export failed',
                message: getErrorMessage(e),
              });
            } finally {
              setExportListLoading(false);
            }
          }}
        >
          {exportListLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Export CSV
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="card p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Summary */}
      {!error && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <div className="p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Pending ICP lines</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(() => {
                    if (tableBusy && summary == null) return '—';
                    if (summary != null && typeof summary.open_leg_count === 'number') {
                      return summary.open_leg_count;
                    }
                    if (!showClearedHistory) return total;
                    return '—';
                  })()}
                </p>
                {!showClearedHistory && summary != null && (
                  <p className="text-xs text-gray-500 mt-1">
                    {summary.total_transactions} line(s) in period (incl. cleared)
                  </p>
                )}
              </div>
            </Card>
            <Card>
              <div className="p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Outstanding IC</p>
                <p className="text-2xl font-bold text-gray-900">{openLegAmountDisplay}</p>
                <p className="text-xs text-gray-500 mt-1">Sum of absolute amounts still open</p>
              </div>
            </Card>
            <Card>
              <div className="p-4 flex flex-col gap-3">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Cleared pairs</p>
                  <p className="text-2xl font-bold text-gray-900">{tableBusy ? '—' : clearedPairsDisplay}</p>
                  <p className="text-xs text-gray-500 mt-1">Mirrored lines netted this period</p>
                </div>
                <Button
                  variant="secondary"
                  className="w-full sm:w-auto"
                  disabled={!effectivePeriodId || worksheetDownloading}
                  onClick={async () => {
                    if (!effectivePeriodId) return;
                    setWorksheetDownloading(true);
                    try {
                      await intercompanyApi.downloadEliminationWorksheet(effectivePeriodId);
                      addNotification({
                        type: 'success',
                        title: 'Download started',
                        message: 'Elimination worksheet CSV saved.',
                      });
                    } catch (e) {
                      addNotification({
                        type: 'error',
                        title: 'Download failed',
                        message: getErrorMessage(e),
                      });
                    } finally {
                      setWorksheetDownloading(false);
                    }
                  }}
                >
                  {worksheetDownloading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Elimination worksheet (CSV)
                </Button>
              </div>
            </Card>
          </div>

          {/* Transactions table */}
          {shouldLoad && tableBusy ? (
            <Card>
              <div className="p-6 flex items-center justify-center text-gray-400 text-sm">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {syncTbLoading ? 'Syncing ICP from trial balance…' : 'Loading intercompany transactions…'}
              </div>
            </Card>
          ) : icTransactions.length === 0 ? (
            <Card>
              <div className="py-16 text-center px-4">
                <Table2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                {!showClearedHistory &&
                summary != null &&
                (summary.total_transactions ?? 0) > 0 &&
                (summary.open_leg_count ?? 0) === 0 ? (
                  <>
                    <h3 className="text-sm font-medium text-gray-900">All ICP lines cleared for this period</h3>
                    <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
                      Pending list is empty because every line is paired. Use &quot;Elimination worksheet&quot; above for the
                      export, or enable &quot;Show cleared history&quot; to review raw lines.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-sm font-medium text-gray-900">No intercompany lines for this period</h3>
                    <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
                      Upload a trial balance for this month with ICP partner codes on IC accounts, map external codes under
                      Entity Code Mapping (source <strong>FCCS</strong>), then pick this period again to sync.
                    </p>
                  </>
                )}
              </div>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="table-header text-left">From Entity</th>
                      <th className="table-header text-left">To Entity</th>
                      <th className="table-header text-right">Amount</th>
                      <th className="table-header text-left">Currency</th>
                      <th className="table-header text-left">Type</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {icTransactions.map((txn) => (
                      <tr key={txn.id} className="hover:bg-gray-50/50">
                        <td className="table-cell text-gray-900">{txn.entity_from_name || txn.entity_from_id}</td>
                        <td className="table-cell text-gray-900">{txn.entity_to_name || txn.entity_to_id}</td>
                        <td className="table-cell text-right font-mono">{Number(txn.amount).toLocaleString()}</td>
                        <td className="table-cell text-gray-500">{txn.currency}</td>
                        <td className="table-cell text-gray-500">{getTransactionTypeLabel(txn.transaction_type ?? '')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="px-4 py-2.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                  <span>
                    Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setPage(1)}
                      disabled={page === 1}
                      className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      First
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <span className="px-3 py-1 text-gray-700 font-medium">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage(totalPages)}
                      disabled={page >= totalPages}
                      className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* Add Transaction Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <TextView variant="heading-2">Add ICP Transaction</TextView>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setAddError(null);
                  setAddForm({ entity_from_id: '', entity_to_id: '', amount: '', transaction_type: 'sale', description: '' });
                }}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {addError && (
                <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-100 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{addError}</p>
                </div>
              )}
              <div>
                <TextView variant="label-xs" className="block mb-1.5">Entity From</TextView>
                <Select
                  value={addForm.entity_from_id ? String(addForm.entity_from_id) : ''}
                  onValueChange={(v) =>
                    setAddForm((f) => ({
                      ...f,
                      entity_from_id: v ? Number(v) : '',
                      entity_to_id: '', // Reset Entity To when From changes
                    }))
                  }
                  options={entities.map((e) => ({ value: String(e.id), label: `${e.name} (${e.code})` }))}
                  placeholder="Select entity"
                  fullWidth
                />
              </div>
              <div>
                <TextView variant="label-xs" className="block mb-1.5">Entity To</TextView>
                <Select
                  value={addForm.entity_to_id ? String(addForm.entity_to_id) : ''}
                  onValueChange={(v) => setAddForm((f) => ({ ...f, entity_to_id: v ? Number(v) : '' }))}
                  options={(() => {
                    const fromId = addForm.entity_from_id ? Number(addForm.entity_from_id) : null;
                    if (!fromId) return [];
                    return entities
                      .filter((e) => Number(e.id) !== fromId)
                      .map((e) => ({ value: String(e.id), label: `${e.name} (${e.code})` }));
                  })()}
                  placeholder={!addForm.entity_from_id ? 'Select Entity From first' : 'Select entity'}
                  fullWidth
                />
              </div>
              <div>
                <TextView variant="label-xs" className="block mb-1.5">Amount</TextView>
                <input
                  type="number"
                  value={addForm.amount}
                  onChange={(e) => setAddForm((f) => ({ ...f, amount: e.target.value }))}
                  placeholder="e.g. 10000"
                  className="input-field text-sm w-full"
                />
              </div>
              <div>
                <TextView variant="label-xs" className="block mb-1.5">Currency (from Entity From)</TextView>
                <TextView variant="body-sm" className="block py-2 px-3 rounded-lg bg-gray-50 border border-gray-200 text-gray-700">
                  {addForm.entity_from_id
                    ? (entities.find((e) => String(e.id) === String(addForm.entity_from_id))?.functional_currency ?? '—')
                    : 'Select Entity From first'}
                </TextView>
              </div>
              <div>
                <TextView variant="label-xs" className="block mb-1.5">Transaction Type</TextView>
                <Select
                  value={addForm.transaction_type}
                  onValueChange={(v) => setAddForm((f) => ({ ...f, transaction_type: v ?? 'sale' }))}
                  options={ICP_TRANSACTION_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                  fullWidth
                />
              </div>
              <div>
                <TextView variant="label-xs" className="block mb-1.5">Description (optional)</TextView>
                <input
                  type="text"
                  value={addForm.description}
                  onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="e.g. IC sale of goods"
                  className="input-field text-sm w-full"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddModal(false);
                  setAddError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={addSaving || !addForm.entity_from_id || !addForm.entity_to_id || !addForm.amount || Number(addForm.amount) === 0}
                onClick={async () => {
                  setAddError(null);
                  setAddSaving(true);
                  try {
                    const entityFrom = entities.find((e) => String(e.id) === String(addForm.entity_from_id));
                    await intercompanyApi.create({
                      period_id: Number(effectivePeriodId),
                      entity_from_id: Number(addForm.entity_from_id),
                      entity_to_id: Number(addForm.entity_to_id),
                      amount: Number(addForm.amount),
                      currency: entityFrom?.functional_currency,
                      transaction_type: addForm.transaction_type,
                      description: addForm.description || undefined,
                    } as unknown as Partial<IntercompanyTransaction>);
                    addNotification({ type: 'success', title: 'Transaction Added', message: 'ICP transaction created successfully.' });
                    setShowAddModal(false);
                    setAddForm({ entity_from_id: '', entity_to_id: '', amount: '', transaction_type: 'sale', description: '' });
                    await refreshData();
                  } catch (e) {
                    const err = e as { detail?: string; message?: string };
                    setAddError(err.detail || err.message || 'Failed to create transaction.');
                  } finally {
                    setAddSaving(false);
                  }
                }}
              >
                {addSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Transaction
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Reconciliation Report Modal */}
      {showReconciliation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <TextView variant="heading-2">Intercompany Reconciliation Report</TextView>
              <button
                onClick={() => {
                  setShowReconciliation(false);
                  setReconciliationReport(null);
                  setReconciliationError(null);
                }}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {reconciliationError && (
                <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-100 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{reconciliationError}</p>
                </div>
              )}
              {reconciliationLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : reconciliationReport ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="p-4 rounded-lg bg-gray-50">
                      <p className="text-xs font-medium text-gray-500">Total Exposure</p>
                      <p className="text-lg font-bold">{reconciliationReport.summary.total_intercompany_exposure}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-green-50">
                      <p className="text-xs font-medium text-gray-500">Match Rate</p>
                      <p className="text-lg font-bold text-green-700">{reconciliationReport.summary.matched_percentage}%</p>
                    </div>
                    <div className="p-4 rounded-lg bg-gray-50">
                      <p className="text-xs font-medium text-gray-500">Matched</p>
                      <p className="text-lg font-bold">{reconciliationReport.summary.total_matched}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-amber-50">
                      <p className="text-xs font-medium text-gray-500">Unmatched</p>
                      <p className="text-lg font-bold text-amber-700">{reconciliationReport.summary.total_unmatched}</p>
                    </div>
                  </div>
                  {reconciliationReport.summary.trend_vs_prior_period && (
                    <div className="p-4 rounded-lg border border-gray-200 bg-blue-50/50">
                      <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Trend vs Prior Period</p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span>
                          Prior match rate: <strong>{reconciliationReport.summary.trend_vs_prior_period.prior_matched_percentage}%</strong>
                        </span>
                        <span>
                          Prior unmatched: <strong>{reconciliationReport.summary.trend_vs_prior_period.prior_total_unmatched ?? '—'}</strong>
                        </span>
                      </div>
                    </div>
                  )}
                  {reconciliationReport.summary.unmatched_by_entity_pair.length > 0 && (
                    <div>
                      <TextView variant="label-xs" className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        Unmatched by Entity Pair
                      </TextView>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="table-header text-left">Entity Pair</th>
                              <th className="table-header text-right">Unmatched</th>
                              <th className="table-header text-right">Difference</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reconciliationReport.summary.unmatched_by_entity_pair.map((row, i) => (
                              <tr key={i} className="border-t border-gray-100">
                                <td className="table-cell">{row.entity_pair}</td>
                                <td className="table-cell text-right">{row.unmatched_count}</td>
                                <td className="table-cell text-right font-mono">{row.difference}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  <div>
                    <TextView variant="label-xs" className="mb-2">Entity Pairs Detail</TextView>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="table-header text-left">From</th>
                            <th className="table-header text-left">To</th>
                            <th className="table-header text-right">Side A Total</th>
                            <th className="table-header text-right">Side B Total</th>
                            <th className="table-header text-right">Difference</th>
                            <th className="table-header text-center">Matched</th>
                            <th className="table-header text-center">Unmatched</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reconciliationReport.entity_pairs.map((pair, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="table-cell">{pair.entity_from_name}</td>
                              <td className="table-cell">{pair.entity_to_name}</td>
                              <td className="table-cell text-right font-mono">{pair.total_from_side_a}</td>
                              <td className="table-cell text-right font-mono">{pair.total_from_side_b}</td>
                              <td className="table-cell text-right font-mono">{pair.difference}</td>
                              <td className="table-cell text-center">{pair.matched_count}</td>
                              <td className="table-cell text-center">{pair.unmatched_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No reconciliation data for this period.</p>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
